import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useTimezone, fetchTimezone } from '../lib/timezone';
import { Wrapper, Status } from '@googlemaps/react-wrapper';

const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

interface PlacesAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect: (place: {
    name: string;
    latitude: number;
    longitude: number;
    country?: string;
  }) => void;
  onTimezoneChange?: (timezone: string) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
}

// Inner component that contains the actual PlacesAutocomplete logic
// This is only rendered when Google Maps API is successfully loaded
const PlacesAutocompleteContent: React.FC<PlacesAutocompleteProps> = ({
  value,
  onChange,
  onPlaceSelect,
  onTimezoneChange,
  placeholder = "Enter a location",
  label = "Location",
  error,
  required = false,
  disabled = false,
}) => {
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiLoaded, setApiLoaded] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  
  // Timezone state
  const [selectedCoordinates, setSelectedCoordinates] = useState<{lat: number; lng: number} | null>(null);
  const { timezoneData, isLoading: isTimezoneLoading, error: timezoneError, refetch: refetchTimezone } = useTimezone(
    selectedCoordinates?.lat || null,
    selectedCoordinates?.lng || null,
    false // Don't auto-fetch, we'll trigger manually
  );
  
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle timezone data updates
  useEffect(() => {
    if (timezoneData && timezoneData.status === 'success' && timezoneData.timezone && onTimezoneChange) {
      console.log('🌍 Timezone data received:', timezoneData);
      console.log('🌍 Calling onTimezoneChange with:', timezoneData.timezone);
      onTimezoneChange(timezoneData.timezone);
    } else if (timezoneData && timezoneData.status === 'error') {
      console.warn('⚠️ Timezone API failed:', timezoneData.error);
    }
  }, [timezoneData, onTimezoneChange]);

  // Initialize Google Places services when API is loaded
  useEffect(() => {
    const initializePlacesAPI = () => {
      console.log('🏗️ Initializing Google Places services...');
      
      if (typeof google !== 'undefined' && google.maps && google.maps.places) {
        try {
          console.log('✅ Creating AutocompleteService...');
          autocompleteService.current = new google.maps.places.AutocompleteService();
          console.log('✅ AutocompleteService created successfully');
          
          // Create a dummy map element for PlacesService
          console.log('✅ Creating dummy map for PlacesService...');
          const dummyMapDiv = document.createElement('div');
          const dummyMap = new google.maps.Map(dummyMapDiv);
          placesService.current = new google.maps.places.PlacesService(dummyMap);
          console.log('✅ PlacesService created successfully');
          
          setApiLoaded(true);
          setApiError(null);
          console.log('🎉 Google Places API services initialized successfully');
        } catch (err) {
          console.error('❌ Error initializing Google Places services:', err);
          setApiError(`Failed to initialize Google Places services: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
      } else {
        console.warn('⚠️ Google Maps API not loaded or Places library not available');
        setApiError('Google Maps API not loaded');
      }
    };

    // Check if Google Maps API is already loaded
    if (typeof google !== 'undefined' && google.maps) {
      initializePlacesAPI();
    } else {
      // Wait for Google Maps API to load with improved retry logic
      let attempts = 0;
      const maxAttempts = 50;
      const baseDelay = 100;
      const maxDelay = 1000;
      
      const checkGoogleAPI = () => {
        attempts++;
        const delay = Math.min(baseDelay * Math.pow(1.1, attempts - 1), maxDelay);
        
        if (typeof google !== 'undefined' && google.maps && google.maps.places) {
          console.log('✅ Google Maps API with Places library is now available');
          initializePlacesAPI();
        } else if (attempts >= maxAttempts) {
          console.error('❌ Timeout waiting for Google Maps API to load with Places library');
          setApiError('Google Maps Places API failed to load. Please check your internet connection.');
        } else {
          setTimeout(checkGoogleAPI, delay);
        }
      };
      
      checkGoogleAPI();
    }
  }, []);

  // Filter suggestions to show only cities/localities
  const filterSuggestions = (predictions: google.maps.places.AutocompletePrediction[]) => {
    return predictions.filter(prediction => {
      const types = prediction.types;
      return types.includes('locality') || 
             types.includes('administrative_area_level_1') ||
             types.includes('administrative_area_level_2') ||
             types.includes('administrative_area_level_3') ||
             types.includes('sublocality') ||
             types.includes('sublocality_level_1') ||
             types.includes('sublocality_level_2') ||
             types.includes('sublocality_level_3') ||
             types.includes('neighborhood') ||
             types.includes('country');
    });
  };

  // Get place details including coordinates and fetch timezone
  const getPlaceDetails = useCallback((placeId: string, prediction: google.maps.places.AutocompletePrediction) => {
    if (!placesService.current) {
      console.warn('⚠️ PlacesService not available');
      return;
    }

    console.log('🔍 Getting place details for:', prediction.structured_formatting.main_text);
    setIsLoading(true);

    const request = {
      placeId,
      fields: [
        'name', 'formatted_address', 'geometry', 'address_components', 
        'types', 'place_id', 'url', 'website', 'formatted_phone_number',
        'international_phone_number', 'opening_hours', 'price_level', 
        'rating', 'user_ratings_total', 'utc_offset', 'vicinity',
        'plus_code', 'photos'
      ]
    };

    placesService.current.getDetails(request, (place, status) => {
      setIsLoading(false);
      
      if (status === google.maps.places.PlacesServiceStatus.OK && place && place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        
        // Extract country from address components
        const countryComponent = place.address_components?.find(
          component => component.types.includes('country')
        );
        
        const country = countryComponent?.long_name || '';
        
        // Update selected coordinates for timezone fetching
        const coordinates = { lat, lng };
        setSelectedCoordinates(coordinates);
        
        // Trigger timezone fetch if callback is provided
        if (onTimezoneChange) {
          fetchTimezone(lat, lng).then(result => {
            if (result.status === 'success' && result.timezone && onTimezoneChange) {
              onTimezoneChange(result.timezone);
            }
          }).catch(error => {
            console.warn('⚠️ Timezone fetch failed:', error);
          });
        }
        
        // Call the original onPlaceSelect callback
        onPlaceSelect({
          name: place.name || prediction.structured_formatting.main_text,
          latitude: lat,
          longitude: lng,
          country: country
        });
      } else {
        console.error('❌ Places service status:', status);
        // Fallback: use prediction data if place details fail
        onPlaceSelect({
          name: prediction.structured_formatting.main_text,
          latitude: 0,
          longitude: 0,
          country: ''
        });
      }
    });
  }, [onPlaceSelect, onTimezoneChange]);

  // Handle input change and fetch suggestions
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    onChange(inputValue);

    if (inputValue.trim() && autocompleteService.current && apiLoaded) {
      setIsLoading(true);
      
      const request = {
        input: inputValue,
        types: ['(cities)'],
        componentRestrictions: undefined,
      };

      autocompleteService.current.getPlacePredictions(request, (predictions, status) => {
        setIsLoading(false);
        
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          const filteredSuggestions = filterSuggestions(predictions);
          setSuggestions(filteredSuggestions);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      });
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [onChange, apiLoaded]);

  // Handle suggestion selection
  const handleSuggestionSelect = useCallback((prediction: google.maps.places.AutocompletePrediction) => {
    const selectedText = prediction.structured_formatting.main_text;
    onChange(selectedText);
    setShowSuggestions(false);
    setSuggestions([]);
    
    getPlaceDetails(prediction.place_id, prediction);
  }, [onChange, getPlaceDetails]);

  // Handle input focus
  const handleInputFocus = useCallback(() => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  }, [suggestions.length]);

  // Handle input blur with delay to allow suggestion click
  const handleInputBlur = useCallback(() => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 150);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
      inputRef.current?.blur();
    }
  }, []);

  // Manual retry function
  const handleRetry = useCallback(() => {
    setApiError(null);
    setApiLoaded(false);
    setRetryCount(prev => prev + 1);
    
    autocompleteService.current = null;
    placesService.current = null;
    
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }, []);

  // Render loading state
  if (!apiLoaded && !apiError) {
    return (
      <div className="relative flex flex-col space-y-2">
        <Label htmlFor="places-autocomplete">{label}</Label>
        <div className="relative">
          <Input
            ref={inputRef}
            id="places-autocomplete"
            type="text"
            value={value}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            placeholder="Loading Google Places..."
            disabled={disabled}
            required={required}
            autoComplete="off"
            className={error ? 'border-destructive' : ''}
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
          </div>
        </div>
        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
      </div>
    );
  }

  // Render error state
  if (apiError) {
    return (
      <div className="relative flex flex-col space-y-2">
        <Label htmlFor="places-autocomplete">{label}</Label>
        <div className="relative">
          <Input
            ref={inputRef}
            id="places-autocomplete"
            type="text"
            value={value}
            onChange={handleInputChange}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            autoComplete="off"
            className={error ? 'border-destructive' : ''}
          />
        </div>
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-md">
          <div className="flex items-start space-x-2">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-destructive" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-destructive">Google Places API Error</h3>
              <div className="mt-1 text-sm text-destructive">
                <p>{apiError}</p>
              </div>
              <div className="mt-3 flex space-x-2">
                <button
                  onClick={handleRetry}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-destructive-foreground bg-destructive hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-destructive"
                >
                  Retry
                </button>
                <button
                  onClick={() => setApiError(null)}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Continue Without Autocomplete
                </button>
              </div>
            </div>
          </div>
        </div>
        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
      </div>
    );
  }

  return (
    <div className="relative flex flex-col space-y-2">
      <Label htmlFor="places-autocomplete">{label}</Label>
      <div className="relative">
        <Input
          ref={inputRef}
          id="places-autocomplete"
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          autoComplete="off"
          className={error ? 'border-destructive' : ''}
        />
        {(isLoading || isTimezoneLoading) && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
          </div>
        )}
        
        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto">
            {suggestions.map((prediction) => (
              <div
                key={prediction.place_id}
                className="px-3 py-2 hover:bg-accent cursor-pointer border-b border-border last:border-b-0 text-popover-foreground"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSuggestionSelect(prediction);
                }}
              >
                <div className="font-medium text-sm text-popover-foreground">
                  {prediction.structured_formatting.main_text}
                </div>
                {prediction.structured_formatting.secondary_text && (
                  <div className="text-xs text-muted-foreground">
                    {prediction.structured_formatting.secondary_text}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
      
      {/* Timezone Error Display */}
      {timezoneError && onTimezoneChange && (
        <div className="text-sm text-muted-foreground">
          <p>⚠️ Timezone could not be determined automatically</p>
          <p className="text-xs">Error: {timezoneError}</p>
        </div>
      )}
      
      {/* Timezone Status Display */}
      {isTimezoneLoading && onTimezoneChange && (
        <div className="text-sm text-muted-foreground">
          🌍 Fetching timezone information...
        </div>
      )}
    </div>
  );
};

// Outer component that wraps the content with Google Maps API loading
const PlacesAutocomplete: React.FC<PlacesAutocompleteProps> = (props) => {
  const { value, onChange, onPlaceSelect, onTimezoneChange, placeholder, label, error, required, disabled } = props;

  // Render function for the Google Maps Wrapper
  const render = useCallback((status: Status) => {
    switch (status) {
      case Status.LOADING:
        return (
          <div className="relative flex flex-col space-y-2">
            <Label htmlFor="places-autocomplete">{label}</Label>
            <div className="relative">
              <Input
                id="places-autocomplete"
                type="text"
                value={value}
                placeholder="Loading Google Places..."
                disabled={true}
                required={required}
                autoComplete="off"
                className={error ? 'border-destructive' : ''}
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              </div>
            </div>
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
          </div>
        );
      case Status.FAILURE:
        return (
          <div className="relative flex flex-col space-y-2">
            <Label htmlFor="places-autocomplete">{label}</Label>
            <div className="relative">
              <Input
                id="places-autocomplete"
                type="text"
                value={value}
                placeholder={placeholder}
                disabled={disabled}
                required={required}
                autoComplete="off"
                className={error ? 'border-destructive' : ''}
              />
            </div>
            <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-md">
              <p className="text-sm">Failed to load Google Places. Please refresh the page.</p>
            </div>
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
          </div>
        );
      case Status.SUCCESS:
        return <PlacesAutocompleteContent {...props} />;
    }
  }, [value, placeholder, label, error, required, disabled, props]);

  if (!apiKey) {
    return (
      <div className="relative flex flex-col space-y-2">
        <Label htmlFor="places-autocomplete">{label}</Label>
        <div className="relative">
          <Input
            id="places-autocomplete"
            type="text"
            value={value}
            placeholder="Google Maps API key not configured"
            disabled={true}
            required={required}
            autoComplete="off"
            className={error ? 'border-destructive' : ''}
          />
        </div>
        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
      </div>
    );
  }

  return (
    <Wrapper apiKey={apiKey} libraries={['places']} render={render} />
  );
};

export default PlacesAutocomplete;
